// Per-request token retrieval; no token cache or browser storage.
export class AttachmentApiError extends Error {
  constructor(detail) {
    super(detail.message);
    this.detail = detail;
  }
}
export async function attachmentJson(response) {
  const body = await response.json();
  if (!response.ok) throw new AttachmentApiError(body.error || {
    message: `HTTP ${response.status}`
  });
  return body;
}
export function createAttachmentClient({
  baseUrl,
  getToken,
  fetchImpl = fetch,
  xhrFactory = () => new XMLHttpRequest()
}) {
  async function request(path, options = {}) {
    const token = await getToken();
    return attachmentJson(await fetchImpl(`${baseUrl}${path}`, {
      ...options,
      headers: {
        Authorization: `Bearer ${token}`,
        ...(options.body ? {
          "Content-Type": "application/json"
        } : {})
      }
    }));
  }
  return {
    capability: () => request("/capabilities/attachments"),
    list: sessionId => request(`/attachments?${new URLSearchParams({
      sessionId
    })}`),
    // The original of a file this conversation still holds (a conversation reopened from History has none in the
    // browser). An <img> or <iframe> cannot send the token, so it is fetched here and handed over as a Blob.
    async content(sessionId, id) {
      const token = await getToken();
      const response = await fetchImpl(`${baseUrl}/attachments/${encodeURIComponent(id)}/content?${new URLSearchParams({
        sessionId
      })}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      if (!response.ok) {
        let body = {};
        try {
          body = await response.json();
        } catch (error) {
          // not JSON: the status below says enough
        }
        throw new AttachmentApiError(body.error || {
          message: `HTTP ${response.status}`
        });
      }
      return response.blob();
    },
    remove: (sessionId, id) => request(`/attachments/${encodeURIComponent(id)}/remove`, {
      method: "POST",
      body: JSON.stringify({
        sessionId
      })
    }),
    async upload(sessionId, file, {
      signal,
      onProgress
    }) {
      const token = await getToken();
      if (signal.aborted) throw new DOMException("已取消", "AbortError");
      return new Promise((resolve, reject) => {
        const xhr = xhrFactory();
        const abort = () => xhr.abort();
        const finish = callback => value => {
          signal.removeEventListener("abort", abort);
          callback(value);
        };
        xhr.open("POST", `${baseUrl}/attachments`);
        xhr.setRequestHeader("Authorization", `Bearer ${token}`);
        xhr.upload.onprogress = event => {
          if (event.lengthComputable) onProgress(Math.round(event.loaded / event.total * 100));
        };
        xhr.onload = finish(() => {
          try {
            const data = JSON.parse(xhr.responseText);
            if (xhr.status !== 201) throw new AttachmentApiError(data.error || {
              message: `HTTP ${xhr.status}`
            });
            resolve(data);
          } catch (error) {
            reject(error);
          }
        });
        xhr.onerror = finish(() => reject(new Error("附件上傳連線中斷。")));
        xhr.onabort = finish(() => reject(new DOMException("已取消；請刷新附件確認容量。", "AbortError")));
        signal.addEventListener("abort", abort, {
          once: true
        });
        const form = new FormData();
        form.append("sessionId", sessionId);
        form.append("file", file);
        xhr.send(form);
      });
    }
  };
}
